import queryString from 'query-string';

import type { ApiGuessedLocation } from '#/api/types/ApiGuessedLocation';
import { geocoderResultToSearchLocation } from '#/components/LocationInput/geocoderResultToSearchLocation';
import type { I18nLocale } from '#/i18n/types/I18nLocale';
import type { SearchLocation } from '#/modules/search/types/SearchLocation';
import { getLocalStorageJsonItem } from '#/utils/localStorage';
import {
  fetchGoogleMapsPlaceDetails,
  fetchGoogleMapsSuggestions,
} from '#/zustand-stores/googleMapsLibrariesStore';

function guessedLocationToSearchLocation(
  guessedLocation: ApiGuessedLocation,
): SearchLocation {
  return {
    ...guessedLocation,
    geoType: 'ipAddress',
    boundingBoxN: null,
    boundingBoxS: null,
    boundingBoxW: null,
    boundingBoxE: null,
  };
}

export async function searchGetInitialLocation(
  guessedLocation: ApiGuessedLocation,
  locale: I18nLocale,
  embedSearchWidgetSearchLocation?: SearchLocation,
): Promise<SearchLocation> {
  const queryParams = queryString.parse(window.location.search);
  const locationParam = queryParams.location;

  if (!locationParam) {
    return (
      (getLocalStorageJsonItem('searchLocation') as SearchLocation) ??
      embedSearchWidgetSearchLocation ??
      guessedLocationToSearchLocation(guessedLocation)
    );
  }

  const url = new URL(window.location.href);
  url.searchParams.delete('location');
  window.history.replaceState({}, '', url.toString());

  try {
    const locationQuery = Array.isArray(locationParam)
      ? (locationParam[0] ?? '')
      : locationParam;

    // special handling of zip code passed through location query param
    const addressTypes = /^\d{5}$/.test(locationQuery)
      ? ['postal_code']
      : undefined;

    const suggestions = await fetchGoogleMapsSuggestions({
      input: locationQuery,
      addressTypes,
    });

    const placeId = suggestions[0]?.placePrediction?.placeId;
    if (placeId) {
      const place = await fetchGoogleMapsPlaceDetails({
        placeId,
        language: locale,
      });

      if (place) {
        const location = await geocoderResultToSearchLocation(place, {});
        return location;
      }
    }
  } catch {
    // do nothing - GoogleMaps error has already been set in the zustand store
  }

  // Fallback to local or guessed
  return (
    (getLocalStorageJsonItem('searchLocation') as SearchLocation) ??
    embedSearchWidgetSearchLocation ??
    guessedLocationToSearchLocation(guessedLocation)
  );
}
